import type { ExistingSnapshotPosition } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./sell-holding-dialog.i18n";

type SellHoldingDialogProps = {
  /** null = closed. */
  holding: ExistingSnapshotPosition | null;
  saving: boolean;
  onClose: () => void;
  /** null = keep (clears the decision); a value sells that much. */
  onSave: (sellAmount: number | null) => void;
};

/**
 * QL-304 — sell-amount editor for an unmatchable holding in an account
 * follow-up. Sell-only by construction: the input is clamped to the frozen
 * holding, and clearing the decision (Keep) sends null. The server re-checks
 * the bounds; this dialog only keeps honest input honest.
 */
export function SellHoldingDialog({ holding, saving, onClose, onSave }: SellHoldingDialogProps) {
  const t = useT(T);
  const { formatNumber } = useRegionalFormat();
  const [value, setValue] = useState<string>("");

  useEffect(() => {
    setValue(holding?.sellAmount != null ? String(holding.sellAmount) : "");
  }, [holding]);

  if (!holding) return null;

  const max = holding.amount;
  const parsed = value.trim() === "" ? null : Number(value);
  const invalid = parsed !== null && (!Number.isFinite(parsed) || parsed <= 0 || parsed > max);

  return (
    <Dialog open onOpenChange={(open) => !saving && !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.sellHoldingTitle}</DialogTitle>
          <DialogDescription>
            {t.sellHoldingDescription(holding.name ?? holding.isin ?? "—", formatNumber(max))}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="sell-amount">{t.sellAmountLabel}</Label>
          <div className="flex items-center gap-2">
            <Input
              id="sell-amount"
              type="number"
              min={0}
              max={max}
              step="any"
              value={value}
              placeholder={t.sellAmountPlaceholder}
              onChange={(e) => setValue(e.target.value)}
            />
            {/* The former row-level sell-all, now a review-then-save step. */}
            <Button
              variant="outline"
              size="sm"
              disabled={saving}
              onClick={() => setValue(String(max))}
            >
              {t.sellAllShortcut}
            </Button>
          </div>
          {invalid && (
            <p className="text-sm text-destructive">{t.sellAmountInvalid(formatNumber(max))}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={onClose}>
            {t.cancel}
          </Button>
          {holding.sellAmount != null && (
            <Button variant="outline" disabled={saving} onClick={() => onSave(null)}>
              {t.keepHolding}
            </Button>
          )}
          <Button disabled={saving || parsed === null || invalid} onClick={() => onSave(parsed)}>
            {t.confirmSell}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
